import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../types';
import { useAuthStore } from '@/features/auth';
import { ConversationPicker } from './ConversationPicker';
import { chatHaptic } from '../utils/chatHaptic';

interface ForwardMessageProps {
  message: ChatMessage;
  isOpen: boolean;
  onClose: () => void;
  onForward: (conversationIds: string[], additionalMessage?: string) => void;
}

export function ForwardMessage({ message, isOpen, onClose, onForward }: ForwardMessageProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [selectedConversationIds, setSelectedConversationIds] = useState<string[]>([]);
  const [additionalMessage, setAdditionalMessage] = useState('');

  const handleToggleConversation = (conversationId: string) => {
    setSelectedConversationIds((prev) =>
      prev.includes(conversationId)
        ? prev.filter((id) => id !== conversationId)
        : prev.length < 5
        ? [...prev, conversationId]
        : prev
    );
  };

  const handleForward = () => {
    if (selectedConversationIds.length > 0) {
      chatHaptic.action('forward');
      onForward(selectedConversationIds, additionalMessage.trim() || undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedConversationIds([]);
    setAdditionalMessage('');
    onClose();
  };

  const getMessagePreview = () => {
    switch (message.type) {
      case 'image':
        return (
          <div className="flex items-center gap-2" style={{ color: 'var(--user-page-text)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" strokeWidth="2" />
            </svg>
            <span>{t('messaging.forward.imageMessage')}</span>
          </div>
        );
      case 'video':
        return (
          <div className="flex items-center gap-2" style={{ color: 'var(--user-page-text)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="2" width="20" height="20" rx="2" ry="2" strokeWidth="2" />
              <polygon points="10,8 16,12 10,16" fill="currentColor" />
            </svg>
            <span>{t('messaging.forward.videoMessage')}</span>
          </div>
        );
      case 'file':
        return (
          <div className="flex items-center gap-2" style={{ color: 'var(--user-page-text)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" strokeWidth="2" />
              <polyline points="13 2 13 9 20 9" strokeWidth="2" />
            </svg>
            <span>{(message.mediaMetadata as Record<string, string>)?.fileName || t('messaging.forward.fileMessage')}</span>
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-2" style={{ color: 'var(--user-page-text)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 18V5l12-2v13" strokeWidth="2" />
              <circle cx="6" cy="18" r="3" strokeWidth="2" />
              <circle cx="18" cy="16" r="3" strokeWidth="2" />
            </svg>
            <span>{t('messaging.forward.audioMessage')}</span>
          </div>
        );
      default:
        return (
          <p className="line-clamp-2" style={{ color: 'var(--user-page-heading)' }}>
            {message.content || t('messaging.forward.emptyMessage')}
          </p>
        );
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-[9998]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
          >
            <div className="border shadow-sm w-full max-w-lg max-h-[85dvh] flex flex-col rounded-3xl overflow-hidden glass-surface" style={{ backgroundColor: 'var(--user-page-card-bg)', borderColor: 'var(--user-page-card-border)' }}>
              {/* Header */}
              <div className="border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: 'var(--user-page-card-border)' }}>
                <h2 className="font-semibold text-xs sm:text-sm">
                  {t('messaging.forward.title')}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 rounded transition-colors"
                  aria-label={t('common.close')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--user-page-heading)' }}>
                    <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Message Preview */}
              <div className="border-b p-4" style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--user-page-text)' }}>
                  {message.senderId === currentUserId
                    ? t('messaging.forward.you')
                    : message.sender?.name || t('messaging.forward.unknown')}
                </div>
                {getMessagePreview()}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Conversation Picker */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--user-page-heading)' }}>
                    {t('messaging.forward.selectRecipients')} ({selectedConversationIds.length}/5)
                  </label>
                  <ConversationPicker
                    selectedIds={selectedConversationIds}
                    onToggle={handleToggleConversation}
                    excludeIds={[message.conversationId]}
                    maxSelections={5}
                  />
                </div>

                {/* Additional Message */}
                <div>
                  <label htmlFor="additional-message" className="block text-sm font-medium mb-2" style={{ color: 'var(--user-page-heading)' }}>
                    {t('messaging.forward.addMessage')} ({t('messaging.forward.optional')})
                  </label>
                  <textarea
                    id="additional-message"
                    value={additionalMessage}
                    onChange={(e) => setAdditionalMessage(e.target.value)}
                    placeholder={t('messaging.forward.addMessagePlaceholder')}
                    className="w-full px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-slate-400 font-medium resize-none"
                    style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs mt-1 text-right" style={{ color: 'var(--user-page-text)' }}>
                    {additionalMessage.length}/500
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t p-4 flex gap-3 justify-end" style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-xl border font-medium transition-colors"
                  style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleForward}
                  disabled={selectedConversationIds.length === 0}
                  className="px-4 py-2 rounded-xl border text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--user-page-heading)', borderColor: 'var(--user-page-card-border)' }}
                >
                  {t('messaging.forward.forwardButton')}
                  {selectedConversationIds.length > 0 && ` (${selectedConversationIds.length})`}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
